import { Box, Button, ButtonGroup, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import { ArrowDropDownIcon } from '@mui/x-date-pickers-pro';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

export type InternalLink =
  | { display: string; visible?: boolean; url: string; onHandleClick?: () => void }
  | { display: string; visible?: boolean; url?: string; onHandleClick: () => void };

export interface InternalLinksProps {
  options: InternalLink[];
}

export const InternalLinks = ({ options }: InternalLinksProps) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  // Filter to only visible links
  const visibleOptions = options.filter((option) => option.visible !== false);

  // Don't render if no visible options
  if (visibleOptions.length === 0) {
    return null;
  }

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  const onHandleClose = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, callback?: () => void) => {
    setOpen(false);
    if (callback) {
      event.stopPropagation();
      event.preventDefault();
      callback();
    }
  };

  return (
    <Box
      className="relative"
      ref={anchorRef}>
      <ButtonGroup
        sx={{
          '& .MuiButtonGroup-grouped': {
            border: '1px solid lightgrey'
          },
          '& .MuiButtonGroup-grouped:not(:last-of-type)': {
            borderRight: '1px solid lightgrey'
          },
          '& .MuiButtonGroup-grouped:hover': {
            backgroundColor: (theme) => theme.palette.action.hover
          }
        }}
        variant="contained"
        disableElevation
        color="grey300">
        <Button
          title="Show options"
          color="grey300"
          size="small"
          aria-controls={open ? 'internal-links-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-label="show options"
          aria-haspopup="menu"
          onClick={handleToggle}>
          <ArrowDropDownIcon
            fontSize="small"
            color="buttonGrey"
          />
        </Button>
      </ButtonGroup>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        placement="bottom-start"
        style={{ zIndex: 1000 }}>
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'left top' : 'left bottom'
            }}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="internal-links-menu">
                  {visibleOptions.map((option, index) => {
                    return (
                      <div key={`${option.display || option.url}-${index}`}>
                        {option.display && (
                          <MenuItem className="w-full flex flex-col">
                            <Link
                              className="flex space-x-1 w-full text-indigo"
                              onClick={(event) => onHandleClose(event, option.onHandleClick)}
                              title={option.display}
                              to={option.url || ''}>
                              <div className="flex items-center text-small">{String(option.display)}</div>
                            </Link>
                          </MenuItem>
                        )}
                      </div>
                    );
                  })}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
};
